import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { usePanelTokens } from "../../utils/panelTokens";

/**
 * The table half of a public dataset page's card/table switch.
 *
 * Deliberately not `Templates/NewListComponent`: that one carries selection
 * checkboxes, pagination, sorting state, an Add button and a `Header`, all of
 * which belong to the signed-in app and none of which a public catalog has. It
 * is also load-bearing for five app pages, so bending it into a second shape
 * would put those at risk to serve a read-only list.
 *
 * `columns` is `[{ key, label, align?, render? }]`. `render(row)` is for a cell
 * that is not just a field — a chip, a formatted duration — and `key` alone
 * covers the rest.
 */
const CollectionTable = ({ columns, rows, rowKey, onRowClick }) => {
  const t = usePanelTokens();

  return (
    // The container scrolls, not the page: a wide table must never make the
    // whole layout slide sideways on a phone.
    <TableContainer sx={{ overflowX: "auto", border: `1px solid ${t.border}`, borderRadius: 2, background: t.surface }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align || "left"}
                sx={{
                  color: t.textFaint,
                  borderColor: t.border,
                  fontSize: 10.5,
                  letterSpacing: ".1em",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={rowKey(row)}
              hover={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{ cursor: onRowClick ? "pointer" : "default" }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  sx={{ color: t.text, borderColor: t.borderSoft, fontSize: 13 }}
                >
                  {column.render ? column.render(row) : <Box component="span">{row[column.key] ?? "—"}</Box>}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CollectionTable;
